import { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';

type PresenceRow = {
  admin_id: string;
  nombre: string;
  email: string;
  role: string;
  tipo_staff: string;
  presente: boolean;
  ultimo_movimiento: string | null;
  ultimo_evento_at: string | null;
  camara_nombre: string | null;
};

const staffLabel = (row: PresenceRow) => {
  if (row.role === 'admin' || row.role === 'gym_admin') return 'Administrador';
  if (row.tipo_staff === 'coach_interno') return 'Coach interno';
  if (row.tipo_staff === 'coach_externo') return 'Coach externo';
  if (row.tipo_staff === 'recepcionista') return 'Recepcionista';
  if (row.tipo_staff === 'nutricionista') return 'Nutricionista';
  return 'Personal general';
};

export default function PresenciaPersonalPage() {
  const { sucursalActiva } = useSucursal();
  const [rows, setRows] = useState<PresenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const load = useCallback(async () => {
    if (!sucursalActiva?.id) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const { data, error: rpcError } = await supabase.rpc('personal_presencia_actual', {
        p_sucursal_id: sucursalActiva.id,
      });
      if (rpcError) throw rpcError;
      setRows((data || []) as PresenceRow[]);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err?.message || 'No se pudo consultar la presencia del personal.');
    } finally {
      setLoading(false);
    }
  }, [sucursalActiva?.id]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 60_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const presentes = useMemo(() => rows.filter((row) => row.presente), [rows]);

  return (
    <AdminLayout>
      <div className="p-0 md:p-2 xl:p-8 space-y-6">
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Personal en el gimnasio</h1>
            <p className="text-gray-600 mt-1">Presencia calculada desde los accesos faciales de entrada y salida.</p>
          </div>
          <button onClick={() => void load()} disabled={loading || !sucursalActiva?.id} className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-800 font-medium hover:bg-gray-50 disabled:opacity-50">
            <i className={`ri-refresh-line mr-2 ${loading ? 'animate-spin' : ''}`} />Actualizar
          </button>
        </header>

        {error && <div className="p-4 rounded-xl border border-red-200 bg-red-50 text-red-700"><i className="ri-error-warning-line mr-2" />{error}</div>}

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5"><p className="text-sm text-gray-500">Trabajando ahora</p><p className="text-3xl font-bold text-emerald-700 mt-2">{presentes.length}</p></div>
          <div className="bg-white rounded-xl border border-gray-200 p-5"><p className="text-sm text-gray-500">Personal habilitado</p><p className="text-3xl font-bold text-gray-900 mt-2">{rows.length}</p></div>
          <div className="bg-white rounded-xl border border-gray-200 p-5"><p className="text-sm text-gray-500">Última actualización</p><p className="text-lg font-semibold text-gray-900 mt-3">{lastRefresh ? lastRefresh.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : '—'}</p></div>
        </section>

        <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b"><h2 className="font-bold text-gray-900">Estado del personal</h2><p className="text-xs text-gray-500 mt-1">Se actualiza automáticamente cada minuto.</p></div>
          {loading && rows.length === 0 ? <div className="p-10 text-center text-gray-500"><i className="ri-loader-4-line animate-spin mr-2" />Consultando accesos…</div> : (
            <div className="divide-y divide-gray-100">
              {rows.map((row) => (
                <article key={row.admin_id} className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                  <span className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${row.presente ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'}`}>{row.nombre?.trim()?.charAt(0)?.toUpperCase() || '?'}</span>
                  <div className="flex-1 min-w-0"><div className="flex items-center gap-2 flex-wrap"><p className="font-semibold text-gray-900">{row.nombre}</p><span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{staffLabel(row)}</span></div><p className="text-sm text-gray-500 truncate">{row.email}</p></div>
                  <div className="md:text-right">
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${row.presente ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}><span className={`w-2 h-2 rounded-full ${row.presente ? 'bg-emerald-500' : 'bg-gray-400'}`} />{row.presente ? 'Trabajando ahora' : 'Fuera del gimnasio'}</span>
                    <p className="text-xs text-gray-500 mt-2">{row.ultimo_evento_at ? `${row.ultimo_movimiento === 'entrada' ? 'Entrada' : 'Salida'}: ${new Date(row.ultimo_evento_at).toLocaleString('es-CL')}` : 'Sin registros faciales'}</p>
                    {row.camara_nombre && <p className="text-xs text-gray-400 mt-0.5">{row.camara_nombre}</p>}
                  </div>
                </article>
              ))}
              {!rows.length && <div className="p-10 text-center text-gray-500">No hay personal habilitado para esta sucursal.</div>}
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
